import React, { useState, useEffect } from 'react';
import './FieldCorrectionModal.css';

export default function FieldCorrectionModal({
  isOpen,
  fieldKeys,        // array of field key strings from the document
  fields,           // current { field_key: value } map
  correctedValue,   // pre-populated value from OCR of drawn zone OR clicked OCR word (5-B)
  initialField,     // optional: pre-select a specific field (per-field pencil entry point)
  lockField = false, // when true, the field is fixed to initialField and cannot be
                     // retargeted. Set for corrections that originate from a per-field
                     // arm (clicked OR typed-fallback): a clicked correction carries
                     // THAT field's inferred anchor, so writing it against a different
                     // field would poison the other field's learned pattern. Default
                     // false preserves the retargetable behavior for any other caller.
  rotation = 0,     // 5-B: page rotation; non-zero → click-to-select is unavailable
  anchorText = null,    // inferred anchor label for a clicked value, or null
  valuePosition = null, // value's position relative to the anchor, or 'region_only', or null (typed)
  onConfirm,        // ({ field_key, corrected_value, typedInstead }) => void
  onCancel,
}) {
  const [selectedField, setSelectedField] = useState('');
  const [value, setValue] = useState('');
  // Tracks whether `value` currently still equals what was pre-populated from
  // a click. Used to decide whether to show the "Or type the value" link —
  // once the user starts typing or clears, the link disappears since the
  // input is already in free-type mode.
  const [hadClickValue, setHadClickValue] = useState(false);
  // D1: "type instead" abandons the click for good — the reviewer said the
  // clicked box is not where the value is, so even retyping identical text
  // must not send the click's location.
  const [typedInstead, setTypedInstead] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Per-field pencil pre-scopes to a specific field. Toolbar pencil
      // (no initialField) defaults to the first available field.
      const preferred = initialField && fieldKeys.includes(initialField)
        ? initialField
        : (fieldKeys[0] || '');
      setSelectedField(preferred);
      setValue(correctedValue || '');
      setHadClickValue(!!correctedValue);
      setTypedInstead(false);
    }
  }, [isOpen, correctedValue, fieldKeys, initialField]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedField) return;
    onConfirm({ field_key: selectedField, corrected_value: value, typedInstead });
  };

  const handleTypeInstead = () => {
    setValue('');
    setHadClickValue(false);
    setTypedInstead(true);
  };

  const formatFieldLabel = (key) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Show the "Or type the value" affordance only when (a) the user opened
  // the modal by clicking a token in the document and (b) they haven't
  // edited the auto-populated value yet. Once they clear or type, the
  // input is already serving as the type-instead path.
  const showTypeInsteadLink = hadClickValue && value === correctedValue && correctedValue !== '';

  // Capture-result preview (informational). Present-tense — this renders DURING
  // editing, before the user confirms and before the corrections POST fires, so
  // it describes what the pending correction WILL teach, never what was saved.
  // Three mutually-exclusive states:
  //   1. anchor found  → the relationship that will be learned
  //   2. click, no anchor (region_only + a pre-filled clicked value) → position
  //      captured but nothing to anchor to
  //   3. typed / no click → value-only; suppressed until the user types a value
  //      (otherwise a just-opened typed-fallback modal would assert an outcome)
  const positionPhrase = {
    right_of_anchor: 'to the right of',
    below_anchor: 'below',
    left_of_anchor: 'to the left of',
    above_anchor: 'above',
  }[valuePosition] || 'near';
  //
  // D1: the click's location is sent only while the value is still the clicked
  // text (the same trim-equal test as utils/correctionPayload.js). Once the
  // reviewer edits it or types instead, nothing positional is sent, and the
  // preview must say so rather than keep promising to teach the anchor.
  const stillClicked = !typedInstead
    && valuePosition != null
    && String(correctedValue ?? '').trim() !== ''
    && value.trim() === String(correctedValue).trim();
  let captureStatus = null;
  if (stillClicked && anchorText) {
    captureStatus = <>This will teach: value appears {positionPhrase} “<strong>{anchorText}</strong>”.</>;
  } else if (stillClicked && valuePosition === 'region_only') {
    captureStatus = 'Position captured — but no nearby label to anchor to.';
  } else if (value !== '') {
    captureStatus = 'Value correction only — no position will be learned.';
  }

  return (
    <div className="fcm-overlay" onClick={onCancel}>
      <div className="fcm-modal" onClick={e => e.stopPropagation()}>
        <div className="fcm-header">
          <span className="fcm-icon">✏️</span>
          <span className="fcm-title">Field Correction</span>
          <button className="fcm-close" onClick={onCancel}>✕</button>
        </div>

        <div className="fcm-body">
          <div className="fcm-hint">
            Select the field to correct and enter the value from the highlighted region.
          </div>

          {/* 5-B: rotation gate hint. OCR bboxes are stored in the un-rotated
              page coordinate space; clicking on word overlays only works when
              the page is rendered un-rotated. If the user has the page
              rotated, click-to-select is disabled in the viewer — surface that
              here so they understand why and can either rotate back or just
              type the value. */}
          {rotation !== 0 && (
            <div className="fcm-rotation-hint">
              Rotate the document back to 0° to use click-to-select, or use the Type Instead option below.
            </div>
          )}

          <div className="fcm-form-row">
            <label className="fcm-label">Field</label>
            <select
              className="fcm-select"
              value={selectedField}
              onChange={e => setSelectedField(e.target.value)}
              disabled={lockField}
            >
              {/* When locked, render only the armed field so the disabled
                  control reads as "this field, deliberately fixed" rather than
                  a truncated list. Unlocked callers keep the full picker. */}
              {(lockField ? [selectedField].filter(Boolean) : fieldKeys).map(k => (
                <option key={k} value={k}>
                  {formatFieldLabel(k)}
                  {fields[k] ? ` — current: ${String(fields[k]).substring(0, 30)}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="fcm-form-row">
            <label className="fcm-label">Correct Value</label>
            <input
              className="fcm-input"
              type="text"
              value={value}
              onChange={e => { setValue(e.target.value); setHadClickValue(false); }}
              placeholder="Enter correct value..."
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleConfirm(); if (e.key === 'Escape') onCancel(); }}
            />
            {showTypeInsteadLink && (
              <button
                type="button"
                className="fcm-type-instead"
                onClick={handleTypeInstead}
                title="Clear the auto-filled value and type the correct one"
              >
                Or type the value
              </button>
            )}
            {captureStatus && <div className="fcm-capture-status">{captureStatus}</div>}
          </div>

          {selectedField && fields[selectedField] && (
            <div className="fcm-current-value">
              Current extracted value: <strong>{String(fields[selectedField])}</strong>
            </div>
          )}
        </div>

        <div className="fcm-footer">
          <button className="fcm-btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className="fcm-btn-confirm"
            onClick={handleConfirm}
            disabled={!selectedField || value.trim() === ''}
          >
            Apply Correction
          </button>
        </div>
      </div>
    </div>
  );
}
